import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import {
  FilterAmenities,
  HotelsRawEventName,
  useEmitCoreHotelsRawEvent,
  useFilterList,
} from "@tenant/hotels";
import { FilterPopover } from "@tenant/ui";
import clsx from "clsx";
import { FilterRating } from "../FilterRating";
import defaultStyles from "./FilterList.module.scss";

export const FilterList = ({ children, className }: PlatformComponentProps) => {
  const styles = useFlightStyles(CoreHotelsComponent.FilterList, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.FilterList);

  const {
    text,
    context: { amenities, starRating, freeCancellation, filterOptions },
    handlers: {
      onRatingChange,
      onCancellationChange,
      onAmenitiesChange,
      onResetRatingFilter,
      onResetAmenitiesFilter,
    },
  } = useFilterList();

  const filters = [
    {
      title: text.starRating.title,
      component: (
        <FilterRating
          selectedValues={starRating}
          onChange={onRatingChange}
          className={cn("filter-popover")}
        />
      ),
      isApplied: starRating.length > 0,
      onReset: onResetRatingFilter,
      key: "star-rating",
    },

    {
      title: text.amenities.title,
      component: (
        <FilterAmenities
          options={filterOptions.amenities}
          selectedValues={amenities}
          onChange={onAmenitiesChange}
          className={cn("filter-popover")}
        />
      ),
      isApplied: amenities.length > 0,
      onReset: onResetAmenitiesFilter,
      key: "amenities",
    },

    {
      title: null,
      component: (
        <button
          type="button"
          key="free-cancellation"
          className={cn("filter-toggle", { applied: freeCancellation })}
          onClick={() => onCancellationChange(!freeCancellation)}
          aria-pressed={freeCancellation}
        >
          {text.freeCancellation.title}
        </button>
      ),
      isApplied: freeCancellation,
      onReset: () => onCancellationChange(false),
      key: "free-cancellation",
    },
  ];

  const emitFilterOpened = useEmitCoreHotelsRawEvent(
    HotelsRawEventName.HotelFilterOpened
  );

  const onOpenChange = (open: boolean) => {
    if (open) {
      emitFilterOpened();
    }
  };

  const filterComponents = filters.map(
    ({ title, component, isApplied, onReset, key }) => {
      if (title != null) {
        return (
          <FilterPopover
            label={title}
            isApplied={isApplied}
            onReset={onReset}
            onOpenChange={onOpenChange}
            key={key}
          >
            {component}
          </FilterPopover>
        );
      } else return component;
    }
  );

  return (
    <div className={clsx(block, className)}>
      {filterComponents}
      {children}
    </div>
  );
};
